import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, Upload, Trash2, ZoomIn, ZoomOut, Pencil, Check } from 'lucide-react';
import { ref, set, remove } from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { database, storage } from '../../firebase/config';

export default function CoverPhoto({ user, bandId, bandData, photoTransform: initialTransform }) {
  const defaultTransform = { x: 50, y: 50, zoom: 1 };
  const [transform, setTransform] = useState(initialTransform || defaultTransform);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);
  const dragStartRef = useRef({ clientX: 0, clientY: 0, startX: 0, startY: 0 });
  const pinchStartRef = useRef({ distance: 0, startZoom: 1 });
  const saveTimeoutRef = useRef(null);

  // Sync with prop changes
  useEffect(() => {
    if (initialTransform) {
      setTransform(initialTransform);
    }
  }, [initialTransform]);

  // ── Auto-save with debounce ─────────────────────────────
  const saveTransform = useCallback((newTransform) => {
    if (!bandId) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const transformRef = ref(database, `bands/${bandId}/bandDocs/bandPhotoTransform`);
        await set(transformRef, newTransform);
      } catch (error) {
        console.error('Failed to save photo position:', error);
      }
    }, 300);
  }, [bandId]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  // ── Clamp helper ────────────────────────────────────────
  const clampTransform = (t) => ({
    x: Math.max(0, Math.min(100, t.x)),
    y: Math.max(0, Math.min(100, t.y)),
    zoom: Math.max(1, Math.min(3, t.zoom)),
  });

  // ── Drag to pan (only in edit mode) ─────────────────────
  const handleDragStart = useCallback((clientX, clientY) => {
    if (!isEditing) return;
    setIsDragging(true);
    dragStartRef.current = { clientX, clientY, startX: transform.x, startY: transform.y };
  }, [transform, isEditing]);

  const handleDragMove = useCallback((clientX, clientY) => {
    if (!isDragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const deltaX = ((clientX - dragStartRef.current.clientX) / rect.width) * 100;
    const deltaY = ((clientY - dragStartRef.current.clientY) / rect.height) * 100;
    const sensitivity = 1 / transform.zoom;
    const newTransform = clampTransform({
      ...transform,
      x: dragStartRef.current.startX - deltaX * sensitivity,
      y: dragStartRef.current.startY - deltaY * sensitivity,
    });
    setTransform(newTransform);
  }, [isDragging, transform]);

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    saveTransform(transform);
  }, [isDragging, transform, saveTransform]);

  // Mouse events
  const onMouseDown = (e) => {
    if (!isEditing) return;
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);

    const onMouseMove = (moveEvent) => handleDragMove(moveEvent.clientX, moveEvent.clientY);
    const onMouseUp = () => {
      handleDragEnd();
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Touch events
  const onTouchStart = (e) => {
    if (!isEditing) return;
    if (e.touches.length === 1) {
      handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
    } else if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      pinchStartRef.current = { distance: dist, startZoom: transform.zoom };
    }
  };

  const onTouchMove = (e) => {
    if (!isEditing) return;
    e.preventDefault();
    if (e.touches.length === 1 && isDragging) {
      handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
    } else if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const scale = dist / pinchStartRef.current.distance;
      const newTransform = clampTransform({
        ...transform,
        zoom: pinchStartRef.current.startZoom * scale,
      });
      setTransform(newTransform);
    }
  };

  const onTouchEnd = () => {
    handleDragEnd();
    saveTransform(transform);
  };

  // Scroll wheel zoom (desktop, only in edit mode)
  const onWheel = useCallback((e) => {
    if (!isEditing || !hasPhoto) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newTransform = clampTransform({
      ...transform,
      zoom: transform.zoom + delta,
    });
    setTransform(newTransform);
    saveTransform(newTransform);
  }, [transform, saveTransform, isEditing]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  // ── Zoom buttons ────────────────────────────────────────
  const handleZoom = (direction) => {
    const delta = direction === 'in' ? 0.25 : -0.25;
    const newTransform = clampTransform({
      ...transform,
      zoom: transform.zoom + delta,
    });
    setTransform(newTransform);
    saveTransform(newTransform);
  };

  // ── Upload ──────────────────────────────────────────────
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user || !bandId) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setUploading(true);
    try {
      const timestamp = Date.now();
      const filename = `bands/${bandId}/bandDocs/bandPhoto/${timestamp}_${file.name}`;
      const fileRef = storageRef(storage, filename);

      const uploadTask = uploadBytesResumable(fileRef, file);

      uploadTask.on('state_changed',
        () => {},
        (error) => {
          console.error('Upload failed:', error);
          alert('Upload failed: ' + error.message);
          setUploading(false);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const photoData = {
              fileName: file.name,
              fileURL: downloadURL,
              storagePath: filename,
              uploadDate: new Date().toISOString(),
              fileSize: (file.size / 1024).toFixed(2),
              uploadedBy: user.uid,
              uploadedByName: user.displayName || user.email
            };

            const photoRef = ref(database, `bands/${bandId}/bandDocs/bandPhoto`);
            await set(photoRef, photoData);

            const newTransform = { x: 50, y: 50, zoom: 1 };
            const transformRef = ref(database, `bands/${bandId}/bandDocs/bandPhotoTransform`);
            await set(transformRef, newTransform);
            setTransform(newTransform);

            setUploading(false);
            setIsEditing(false);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          } catch (error) {
            console.error('Failed to save photo:', error);
            alert('Failed to save photo: ' + error.message);
            setUploading(false);
          }
        }
      );
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload error: ' + error.message);
      setUploading(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────
  const handleDeletePhoto = async () => {
    if (!bandId || !bandData?.bandDocs?.bandPhoto) return;

    try {
      const storagePath = bandData.bandDocs.bandPhoto.storagePath;
      if (storagePath) {
        const fileRef = storageRef(storage, storagePath);
        await deleteObject(fileRef).catch(() => {});
      }

      const photoRef = ref(database, `bands/${bandId}/bandDocs/bandPhoto`);
      await remove(photoRef);

      const transformRef = ref(database, `bands/${bandId}/bandDocs/bandPhotoTransform`);
      await remove(transformRef);
      setTransform(defaultTransform);

      setShowDeleteConfirm(false);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to delete photo:', error);
      alert('Failed to delete photo: ' + error.message);
    }
  };

  const hasPhoto = !!bandData?.bandDocs?.bandPhoto?.fileURL;

  const photoStyle = hasPhoto ? {
    backgroundImage: `url(${bandData.bandDocs.bandPhoto.fileURL})`,
    backgroundPosition: `${transform.x}% ${transform.y}%`,
    backgroundSize: `${transform.zoom * 100}%`,
    backgroundRepeat: 'no-repeat',
  } : {};

  return (
    <div
      ref={containerRef}
      className="relative rounded-card overflow-hidden select-none"
      style={{ minHeight: '280px' }}
    >
      {/* Background Image or Gradient */}
      {hasPhoto ? (
        <div
          className={`absolute inset-0 ${isEditing ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-pointer'}`}
          style={photoStyle}
          onClick={() => { if (!isEditing && !isDragging) setIsEditing(true); }}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-r from-brand to-brand-magenta">
          <div className="relative z-10 flex flex-col items-center justify-center h-full">
            <Camera className="w-16 h-16 text-white/50 mb-4" />
            <p className="text-white/70 text-lg mb-4">Add your band photo</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm transition rounded-pill px-6 h-btn font-semibold flex items-center gap-2"
            >
              <Upload className="w-5 h-5" />
              {uploading ? 'Uploading...' : 'Upload Photo'}
            </button>
          </div>
        </div>
      )}

      {/* Edit mode: dim overlay */}
      {hasPhoto && isEditing && !showDeleteConfirm && (
        <div className="absolute inset-0 bg-black/30 z-10 pointer-events-none" />
      )}

      {/* Edit button — only shows when NOT editing */}
      {hasPhoto && !isEditing && (
        <button
          onClick={() => setIsEditing(true)}
          className="absolute top-4 right-4 z-20 bg-black/50 hover:bg-brand backdrop-blur-sm transition p-2 rounded-full"
          title="Edit Photo"
        >
          <Pencil className="w-5 h-5" />
        </button>
      )}

      {/* ── Edit mode controls ─────────────────────────── */}
      {hasPhoto && isEditing && !showDeleteConfirm && (
        <>
          {/* Help text */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 bg-black/50 backdrop-blur-sm rounded-pill px-3 py-1 pointer-events-none">
            <p className="text-white/60 text-xs">Drag to reposition • Scroll to zoom</p>
          </div>

          {/* Upload — top left */}
          <button
            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
            disabled={uploading}
            className="absolute top-4 left-4 z-20 bg-black/50 hover:bg-brand backdrop-blur-sm transition p-2 rounded-full"
            title={uploading ? 'Uploading...' : 'Change Photo'}
          >
            <Upload className="w-5 h-5" />
          </button>

          {/* Zoom — top center */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 flex gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); handleZoom('out'); }}
              disabled={transform.zoom <= 1}
              className="bg-black/50 hover:bg-brand backdrop-blur-sm transition p-2 rounded-full disabled:opacity-30"
              title="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleZoom('in'); }}
              disabled={transform.zoom >= 3}
              className="bg-black/50 hover:bg-brand backdrop-blur-sm transition p-2 rounded-full disabled:opacity-30"
              title="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>

          {/* Delete — top right */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(true); }}
            className="absolute top-4 right-14 z-20 bg-black/50 hover:bg-red-600 backdrop-blur-sm transition p-2 rounded-full"
            title="Delete Photo"
          >
            <Trash2 className="w-5 h-5" />
          </button>

          {/* Done — top right corner */}
          <button
            onClick={(e) => { e.stopPropagation(); setIsEditing(false); }}
            className="absolute top-4 right-4 z-20 bg-brand hover:bg-brand-hover backdrop-blur-sm transition p-2 rounded-full"
            title="Done"
          >
            <Check className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 z-30 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center gap-4">
          <p className="text-white font-semibold">Delete band photo?</p>
          <div className="flex gap-3">
            <button
              onClick={handleDeletePhoto}
              className="bg-red-600 hover:bg-red-500 transition px-6 h-[42px] font-semibold flex items-center gap-2 rounded-card-btn text-sm"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="bg-transparent border border-surface-border-light hover:border-surface-border transition px-6 h-[42px] font-semibold rounded-card-btn text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handlePhotoUpload}
        className="hidden"
      />
    </div>
  );
}
